import React, { Fragment, memo } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, number, object, oneOfType, shape, string } from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'

import Lines from './Components/Lines/Lines'
import List from './Components/List/List'
import useVariationDetails from './useVariationDetails'
import useVisibleCount from './useVisibleCount'

const valueClassName = index => classnames(
  'variation-info__value',
  index === 0 && 'variation-info__value--primary',
)

const renderValues = (details, itemProps = {}) => details.map(({ label, value, shortValue }, index) => (
  <Fragment key={label}>
    {index > 0 && <span className="variation-info__dot" aria-hidden="true" />}
    <span className={valueClassName(index)} {...itemProps}>{shortValue ?? value}</span>
  </Fragment>
))

function VariationInfo({ variation, isCompact = false }) {
  const { details, lines } = useVariationDetails(variation)
  const [rowRef, measureRef, visibleCount] = useVisibleCount(details.length, lines.join('|'))
  const hiddenCount = details.length - visibleCount

  if (details.length === 0) {
    return null
  }

  return (
    <>
      <DesktopOnlyTooltip
        id={`variation-info-${variation.id}`}
        content={<Lines lines={lines} />}
        place="top"
        containerClass="variation-info"
        className="variation-info__trigger"
        focusable
        ariaLabel={lines.join(', ')}
      >
        <span
          ref={rowRef}
          className={classnames('variation-info__row', isCompact && 'variation-info__row--compact', 'cgt-caption')}
        >
          {renderValues(details.slice(0, visibleCount))}
          {hiddenCount > 0 && (
            <>
              <span className="variation-info__dot" aria-hidden="true" />
              <span className="variation-info__more">+{hiddenCount}</span>
            </>
          )}
          <span ref={measureRef} className="variation-info__measure" aria-hidden="true">
            {renderValues(details, { 'data-measure-item': true })}
            <span className="variation-info__dot" />
            <span className="variation-info__more" data-measure-counter>+{details.length}</span>
          </span>
        </span>
      </DesktopOnlyTooltip>
      <List lines={lines} isCompact={isCompact} />
    </>
  )
}

VariationInfo.propTypes = {
  variation: shape({
    id: oneOfType([string, number]).isRequired,
    providerModelId: string,
    providerRunAttributes: object,
  }).isRequired,
  isCompact: bool,
}

export default memo(VariationInfo)
